import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2, LoaderCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
import { supabase } from '../lib/supabase';
import { appUrl } from '../lib/site';
import './learn-more.css';
import './request-access.css';

export const RequestAccess = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState<'idle' | 'sending' | 'success'>('idle');
  const [error, setError] = useState('');
  const busy = useRef(false);
  const requestId = useRef(crypto.randomUUID());
  const successHeading = useRef<HTMLHeadingElement>(null);
  const source = params.get('source') === 'real-estate' ? 'real-estate' : params.get('source') === 'dashboard' ? 'dashboard' : 'marketing';

  useEffect(() => {
    const previous = document.title;
    document.title = 'Request access | ADarveo';
    return () => { document.title = previous; };
  }, []);
  useEffect(() => { if (state === 'success') successHeading.current?.focus(); }, [state]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy.current) return;
    const form = new FormData(event.currentTarget);
    const fields = Object.fromEntries(['name', 'email', 'phone', 'company', 'service', 'goals', 'timeline', 'website'].map(key => [key, String(form.get(key) || '').trim()]));
    busy.current = true;
    setState('sending');
    setError('');
    try {
      if (!supabase) throw new Error('Form unavailable');
      const { data, error: requestError } = await supabase.functions.invoke('submit-account-request', {
        body: { ...fields, requestId: requestId.current, source, consent: form.get('consent') === 'on' },
        signal: AbortSignal.timeout(25000)
      });
      if (requestError?.context instanceof Response && [400, 429].includes(requestError.context.status)) {
        const details = await requestError.context.json().catch(() => null);
        setError(details?.error || 'Please check your details and try again.');
        setState('idle');
        return;
      }
      if (requestError || data?.ok !== true) throw new Error('Submission failed');
      setState('success');
    } catch {
      setError('We couldn’t submit your request. Please try again, or email signup@adarveo.com.');
      setState('idle');
    } finally { busy.current = false; }
  };

  return <div className="enquiry-page access-page">
    <header className="enquiry-header">
      <Link to="/" aria-label="ADarveo home"><Logo className="enquiry-logo" /></Link>
      <a href={source === 'real-estate' ? 'https://realestate.adarveo.com' : appUrl}>Already have an account? Sign in</a>
    </header>
    <main className="enquiry-main">
      <section className="enquiry-story" aria-labelledby="access-title">
        <p className="enquiry-eyebrow">Let’s talk about your next project</p>
        <h1 id="access-title">What would you like <span>to bring to life?</span></h1>
        <p className="enquiry-intro">We’re welcoming new customers personally. Tell us a little about your plans and we’ll get back to you to discuss how ADarveo can help.</p>
        <p className="access-note">Submitting this form requests a conversation. It doesn’t create an account or start a paid subscription.</p>
      </section>
      <section className="enquiry-card" aria-labelledby="access-form-title">
        {state === 'success' ? <div className="enquiry-success" role="status">
          <span className="enquiry-success-icon"><CheckCircle2 aria-hidden="true" /></span>
          <h2 id="access-form-title" ref={successHeading} tabIndex={-1}>Thanks. We’ll be in touch.</h2>
          <p>Your details and goals have been saved. We’ll contact you using the details you provided to discuss the next steps.</p>
          <Link className="enquiry-home-link" to="/">Back to ADarveo <ArrowRight aria-hidden="true" /></Link>
        </div> : <>
          <h2 id="access-form-title">Request access</h2>
          <p className="enquiry-form-intro">A few details will help us prepare for our conversation.</p>
          <form onSubmit={submit} aria-busy={state === 'sending'}>
            <fieldset disabled={state === 'sending'}>
              <label htmlFor="access-name">Your name</label>
              <input id="access-name" name="name" autoComplete="name" required maxLength={120} />
              <label htmlFor="access-email">Email address</label>
              <input id="access-email" name="email" type="email" autoComplete="email" required maxLength={254} />
              <label htmlFor="access-phone">Phone number <span>(optional)</span></label>
              <input id="access-phone" name="phone" type="tel" autoComplete="tel" maxLength={40} />
              <label htmlFor="access-company">Company or organisation <span>(optional)</span></label>
              <input id="access-company" name="company" autoComplete="organization" maxLength={160} />
              <label htmlFor="access-service">What are you interested in?</label>
              <select id="access-service" name="service" defaultValue={source === 'real-estate' ? 'Real estate displays' : ''} required>
                <option value="" disabled>Select an option</option>
                {['AR advertising and campaigns', 'Real estate displays', 'Product packaging and retail', 'Events and experiences', 'Something else / not sure yet'].map(value => <option key={value}>{value}</option>)}
              </select>
              <label htmlFor="access-goals">What would you like to achieve?</label>
              <textarea id="access-goals" name="goals" rows={4} required minLength={10} maxLength={2000} placeholder="Tell us about your project, your audience, and what you’d like them to do." />
              <label htmlFor="access-timeline">When would you like to get started?</label>
              <select id="access-timeline" name="timeline" defaultValue="" required>
                <option value="" disabled>Select a timeframe</option>
                {['As soon as possible', 'Within a month', 'In 1–3 months', 'Just exploring'].map(value => <option key={value}>{value}</option>)}
              </select>
              <div className="enquiry-honeypot" aria-hidden="true"><label htmlFor="access-website">Leave this field empty</label><input id="access-website" name="website" tabIndex={-1} autoComplete="off" /></div>
              <label className="access-consent" htmlFor="access-consent"><input id="access-consent" name="consent" type="checkbox" required /><span>I agree that ADarveo may contact me about this request. <Link to="/privacy">Privacy policy</Link>.</span></label>
              {error && <p className="enquiry-error" role="alert">{error}</p>}
              <button type="submit" className="enquiry-submit" disabled={state === 'sending'}>{state === 'sending' ? <>Sending your request <LoaderCircle className="enquiry-spinner" aria-hidden="true" /></> : <>Request a conversation <ArrowRight aria-hidden="true" /></>}</button>
            </fieldset>
          </form>
        </>}
      </section>
    </main>
    <footer className="enquiry-footer"><span>© {new Date().getFullYear()} ADarveo</span><a href="mailto:signup@adarveo.com">signup@adarveo.com</a></footer>
  </div>;
};
